/**
 * SSRFDetector — Detection of Server-Side Request Forgery attempt indicators
 * in client-side outbound requests.
 *
 * Detects two patterns:
 *   1. Fetch/XHR destination URLs pointing to internal/restricted targets.
 *   2. URL parameters or body fields whose values reference internal targets
 *      (proxy SSRF pattern: /api/proxy?url=http://169.254.169.254/).
 *
 * Only fires on absolute URLs with internal hostnames or dangerous protocols.
 * Does NOT fire on relative URLs (destination cannot be confirmed without resolution).
 * Does NOT run on page load (browser navigating to an internal IP is a
 * legitimate developer scenario and would cause false positives).
 *
 * CWE-918 | OWASP A10:2021 – Server-Side Request Forgery
 */

/**
 * Protocol schemes that must never appear as the scheme of an outbound API call.
 * Legitimate browser fetch() calls always use http or https.
 */
const DANGEROUS_PROTOCOL = /^(file|gopher|dict|ftp|tftp|ldap|ldaps|netdoc):\/\//i;

/**
 * Parameter/field names commonly used to pass a target URL to a server-side
 * fetch proxy. Only checked when the resolved value is an internal target.
 */
const SSRF_PARAM_NAMES = /^(url|uri|src|source|target|dest|destination|redirect|next|return|to|goto|host|proxy|site|path|link|feed|fetch|endpoint|backend|remote|ref|load|open|callback|service|out)$/i;

/**
 * Converts a 32-bit unsigned integer to dotted-decimal IPv4 string.
 * @param {number} n — unsigned 32-bit integer
 * @returns {string}
 */
function numToIPv4(n) {
	const u = n >>> 0;
	return `${(u >>> 24) & 0xff}.${(u >>> 16) & 0xff}.${(u >>> 8) & 0xff}.${u & 0xff}`;
}

/**
 * Classifies a 32-bit IPv4 address (as an unsigned integer) into an SSRF variant.
 * Covers loopback, cloud metadata, and all RFC 1918 / RFC 6598 private ranges.
 *
 * @param {number} n — unsigned 32-bit integer representation of the IPv4 address
 * @returns {{ variant: string, evidence: string }|null}
 */
function classifyNumericIP(n) {
	const u = n >>> 0;
	const b0 = (u >>> 24) & 0xff;
	const b1 = (u >>> 16) & 0xff;
	const ip = numToIPv4(u);

	// Loopback: 127.0.0.0/8 or 0.0.0.0 (INADDR_ANY — resolves to loopback on most OS)
	if (b0 === 127 || u === 0) return { variant: 'loopback', evidence: ip };
	// Link-local / cloud metadata: 169.254.0.0/16
	if (b0 === 169 && b1 === 254) return { variant: 'cloud_metadata', evidence: ip };
	// RFC 1918: 10.0.0.0/8
	if (b0 === 10) return { variant: 'internal_ip', evidence: ip };
	// RFC 1918: 172.16.0.0/12
	if (b0 === 172 && b1 >= 16 && b1 <= 31) return { variant: 'internal_ip', evidence: ip };
	// RFC 1918: 192.168.0.0/16
	if (b0 === 192 && b1 === 168) return { variant: 'internal_ip', evidence: ip };
	// RFC 6598 carrier-grade NAT (AWS shared space): 100.64.0.0/10
	if (b0 === 100 && b1 >= 64 && b1 <= 127) return { variant: 'internal_ip', evidence: ip };

	return null;
}

/**
 * Extracts the lowercase hostname from an absolute URL string.
 * Returns null for relative URLs or unparseable strings — prevents
 * false positives when relative paths are analysed.
 *
 * @param {string} urlString
 * @returns {string|null}
 */
function extractHostname(urlString) {
	if (!urlString || typeof urlString !== 'string') return null;
	// Only process URLs with an explicit scheme (absolute URLs).
	// Relative paths like /api/data or ../other have no deterministic hostname.
	if (!/^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//i.test(urlString)) return null;
	try {
		return new URL(urlString).hostname.toLowerCase();
	} catch (_) {
		return null;
	}
}

/**
 * Checks whether a hostname belongs to a private/internal/restricted network range.
 *
 * Ranges checked:
 *   - Loopback:         127.x.x.x, localhost, 0.0.0.0, ::1
 *   - Cloud metadata:   169.254.x.x (AWS/GCP/Azure IMDS), metadata.google.internal,
 *                       100.100.100.200 (Alibaba Cloud)
 *   - RFC 1918:         10.x.x.x, 172.16–31.x.x, 192.168.x.x
 *   - AWS shared range: 100.64.0.0/10
 *
 * @param {string} hostname
 * @returns {{ variant: string, evidence: string }|null}
 */
function classifyHostname(hostname) {
	if (!hostname) return null;

	// Loopback
	if (
		/^localhost$/i.test(hostname) ||
		/^0\.0\.0\.0$/.test(hostname) ||
		/^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
		hostname === '::1' ||
		hostname === '[::1]'
	) {
		return { variant: 'loopback', evidence: hostname };
	}

	// Cloud metadata endpoints
	if (
		/^169\.254\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
		/^metadata\.google\.internal$/i.test(hostname) ||
		hostname === '100.100.100.200'
	) {
		return { variant: 'cloud_metadata', evidence: hostname };
	}

	// RFC 1918 private ranges + AWS shared address space (RFC 6598, 100.64.0.0/10)
	if (
		/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
		/^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
		/^192\.168\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
		/^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\.\d{1,3}\.\d{1,3}$/.test(hostname)
	) {
		return { variant: 'internal_ip', evidence: hostname };
	}

	// Kubernetes internal service discovery — API server and built-in service hostnames
	if (
		/^kubernetes\.default(?:\.svc(?:\.cluster\.local)?)?$/i.test(hostname) ||
		/^kube-(?:apiserver|scheduler|controller-manager|dns|proxy)(?:\.\w+)*$/i.test(hostname)
	) {
		return { variant: 'kubernetes_metadata', evidence: hostname };
	}

	// Common internal service hostnames with no public TLD — only resolvable inside a cluster/VPC
	if (
		/^(?:redis|memcached|elasticsearch|kibana|cassandra|mongodb|rabbitmq|kafka|zookeeper|consul|vault|jenkins|sonarqube|nexus|grafana|prometheus|mysql|postgres|postgresql|mssql)$/.test(hostname)
	) {
		return { variant: 'internal_service_probe', evidence: hostname };
	}

	// Numeric IP representations that bypass hostname string checks — PATT SSRF bypass techniques.

	// Hex dword: http://0x7f000001/ = 127.0.0.1
	if (/^0x[0-9a-f]+$/i.test(hostname)) {
		const n = parseInt(hostname, 16);
		if (Number.isSafeInteger(n) && n >= 0 && n <= 0xffffffff) {
			const hit = classifyNumericIP(n);
			if (hit) return hit;
		}
	// Decimal dword: http://2130706433/ = 127.0.0.1  (also catches http://0/)
	} else if (/^\d+$/.test(hostname)) {
		const n = parseInt(hostname, 10);
		if (Number.isSafeInteger(n) && n >= 0 && n <= 0xffffffff) {
			const hit = classifyNumericIP(n);
			if (hit) return hit;
		}
	}

	// Octal-dotted notation: http://0177.0.0.1/ = 127.0.0.1
	// Browsers parse octets with a leading zero as octal (implementation-defined but common).
	if (/^0[0-7]/.test(hostname)) {
		const parts = hostname.split('.');
		if (parts.length === 4) {
			try {
				const dec = parts.map(p =>
					p.startsWith('0') && p.length > 1 ? parseInt(p, 8) : parseInt(p, 10)
				);
				if (dec.every(p => !isNaN(p) && p >= 0 && p <= 255)) {
					const num = ((dec[0] << 24) | (dec[1] << 16) | (dec[2] << 8) | dec[3]) >>> 0;
					const hit = classifyNumericIP(num);
					if (hit) return hit;
				}
			} catch (_) {}
		}
	}

	return null;
}

export class SSRFDetector {
	/**
	 * Analyzes an outbound request for SSRF indicators.
	 *
	 * Checks, in order:
	 *   1. Dangerous protocol in the destination URL (file://, gopher://, …)
	 *   2. Internal/restricted destination hostname
	 *   3. URL query parameters whose values point to an internal target
	 *   4. Request body parameters whose values point to an internal target
	 *
	 * @param {{ url: string, body?: string, currentHostname?: string }} options
	 *   currentHostname — window.location.hostname of the current page. When provided,
	 *   same-origin requests (destination hostname === currentHostname) are excluded from
	 *   the direct-destination check to avoid false positives in dev/localhost environments.
	 * @returns {{ detected: true, type: 'SSRF', variant: string, evidence: string, confidence: 'high'|'medium' }
	 *         |{ detected: false }}
	 */
	analyze({ url, body, currentHostname }) {
		if (url && typeof url === 'string') {
			// 1. Protocol smuggling: file://, gopher://, dict://, etc.
			if (DANGEROUS_PROTOCOL.test(url)) {
				const match = url.match(DANGEROUS_PROTOCOL);
				return {
					detected: true,
					type: 'SSRF',
					variant: 'protocol_smuggling',
					evidence: match ? match[0] : url.substring(0, 32),
					confidence: 'high'
				};
			}

			// 2. Direct destination is an internal/restricted host.
			// Skip when the destination hostname matches the current page host — a same-origin
			// request from a dev/localhost app to its own API is never SSRF.
			const hostname = extractHostname(url);
			if (hostname && hostname !== (currentHostname || '').toLowerCase()) {
				const hit = classifyHostname(hostname);
				if (hit) {
					return {
						detected: true,
						type: 'SSRF',
						variant: hit.variant,
						evidence: hit.evidence,
						confidence: 'high'
					};
				}
			}

			// 3. URL query parameters contain an internal target value (proxy SSRF)
			const paramHit = this._scanUrlParams(url);
			if (paramHit) {
				return {
					detected: true,
					type: 'SSRF',
					variant: 'ssrf_param',
					evidence: paramHit,
					confidence: 'high'
				};
			}
		}

		// 4. Request body fields contain an internal target value
		if (body && typeof body === 'string' && body.length > 0) {
			const bodyHit = this._scanBody(body);
			if (bodyHit) {
				return {
					detected: true,
					type: 'SSRF',
					variant: 'ssrf_param',
					evidence: bodyHit,
					confidence: 'medium'
				};
			}
		}

		return { detected: false };
	}

	/**
	 * Scans URL query parameters for SSRF-sensitive parameter values.
	 * @param {string} urlString
	 * @returns {string|null}
	 */
	_scanUrlParams(urlString) {
		let search;
		try {
			search = new URL(urlString).search;
		} catch (_) {
			const idx = urlString.indexOf('?');
			if (idx === -1) return null;
			search = urlString.substring(idx);
		}
		if (!search || search === '?') return null;
		return this._scanQueryString(search);
	}

	/**
	 * Scans a query string for parameter values that reference internal targets.
	 * Only inspects parameters whose names are in SSRF_PARAM_NAMES.
	 * @param {string} queryString  May include the leading '?'.
	 * @returns {string|null}
	 */
	_scanQueryString(queryString) {
		let params;
		try {
			params = new URLSearchParams(queryString);
		} catch (_) {
			return null;
		}
		for (const [name, value] of params.entries()) {
			if (!SSRF_PARAM_NAMES.test(name)) continue;
			const hit = this._checkInternalValue(value);
			if (hit) return hit;
		}
		return null;
	}

	/**
	 * Scans a request body string (URL-encoded or JSON) for SSRF-sensitive values.
	 * @param {string} body
	 * @returns {string|null}
	 */
	_scanBody(body) {
		// URL-encoded body: key=value&key=value
		if (body.indexOf('=') !== -1 && body.charAt(0) !== '{' && body.charAt(0) !== '[') {
			return this._scanQueryString('?' + body);
		}
		// JSON body
		if (body.charAt(0) === '{' || body.charAt(0) === '[') {
			try {
				const parsed = JSON.parse(body);
				if (typeof parsed === 'object' && parsed !== null) {
					return this._scanJsonObject(parsed, 0);
				}
			} catch (_) {
				// Not valid JSON — skip
			}
		}
		return null;
	}

	/**
	 * Recursively scans a parsed JSON object for SSRF-sensitive field values.
	 * Capped at 3 levels deep to avoid performance issues on deeply nested payloads.
	 * @param {object} obj
	 * @param {number} depth
	 * @returns {string|null}
	 */
	_scanJsonObject(obj, depth) {
		if (depth > 3 || typeof obj !== 'object' || obj === null) return null;
		for (const [key, value] of Object.entries(obj)) {
			if (typeof value === 'string') {
				if (SSRF_PARAM_NAMES.test(key)) {
					const hit = this._checkInternalValue(value);
					if (hit) return hit;
				}
			} else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
				const nested = this._scanJsonObject(value, depth + 1);
				if (nested) return nested;
			}
		}
		return null;
	}

	/**
	 * Checks if a string value is an internal/restricted URL.
	 * Handles protocol smuggling and internal hostname detection.
	 * @param {string} value
	 * @returns {string|null} evidence string, or null if not internal
	 */
	_checkInternalValue(value) {
		if (!value || typeof value !== 'string' || value.length < 7) return null;
		// Protocol smuggling in param value (e.g. file:// passed as a redirect target)
		if (DANGEROUS_PROTOCOL.test(value)) {
			const match = value.match(DANGEROUS_PROTOCOL);
			return match ? match[0] : value.substring(0, 32);
		}
		// Internal hostname in param value (e.g. http://169.254.169.254/ passed as url=)
		const hostname = extractHostname(value);
		if (hostname) {
			const hit = classifyHostname(hostname);
			if (hit) return hit.evidence;
		}
		return null;
	}
}

export default SSRFDetector;
